import { useEffect, useRef, useState } from "react";
import { landingQrPath, landingQrTarget } from "../data/landing-qr.js";
import "../styles/desktop-landing.css";

const dismissKey = "fairway.landing.dismissed";

function shouldShow(): boolean {
  try {
    if (sessionStorage.getItem(dismissKey) === "1") return false;
  } catch {
    return false;
  }
  return window.matchMedia("(min-width: 1024px)").matches;
}

/**
 * Desktop-only landing: a QR code that points phones to the app, with a
 * "continue in browser" escape hatch. Dismissed per session.
 */
export function DesktopLanding(props: {
  onSignIn?: () => void;
}): React.JSX.Element | null {
  const [visible, setVisible] = useState(shouldShow);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (visible) dialog.current?.showModal();
  }, [visible]);
  useEffect(() => {
    const query = window.matchMedia("(min-width: 1024px)");
    const update = () => setVisible((current) => current && query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  if (!visible) return null;
  const dismiss = () => {
    try {
      sessionStorage.setItem(dismissKey, "1");
    } catch {
      /* optional */
    }
    setVisible(false);
  };
  return (
    <dialog
      ref={dialog}
      className="frl"
      aria-label="Welcome to Fairway Rivalry"
      onCancel={dismiss}
    >
      <div className="frl-art" aria-hidden="true" />
      <LandingHead
        onSignIn={() => {
          dismiss();
          props.onSignIn?.();
        }}
      />
      <section className="frl-hero">
        <LandingCopy onDismiss={dismiss} />
        <p className="frl-art-caption">Some scores stay with you.</p>
      </section>
    </dialog>
  );
}

function LandingHead(props: { onSignIn(): void }): React.JSX.Element {
  return (
    <div className="frl-head">
      <div className="frl-brand">
        <img src="/img/logo-header.png" alt="" width="44" height="44" />
        <span>Fairway Rivalry</span>
      </div>
      <button type="button" className="frl-signin" onClick={props.onSignIn}>
        Sign in
      </button>
    </div>
  );
}

function LandingCopy(props: { onDismiss(): void }): React.JSX.Element {
  return (
    <div className="frl-copy">
      <p className="frl-eyebrow">THE ROUND IS JUST THE BEGINNING</p>
      <h1 className="frl-h1">
        Every rivalry starts with <em>one round.</em>
      </h1>
      <p className="frl-sub">
        Keep score. Know who wins, where, and when. Build a history with every
        rival you play.
      </p>
      <div className="frl-ctas">
        <button type="button" className="frl-cta" onClick={props.onDismiss}>
          Open Fairway Rivalry <span aria-hidden="true">→</span>
        </button>
        <p>Play a round. Find your edge. Come back for the rematch.</p>
      </div>
      <div className="frl-qrcard">
        <div className="frl-qr">
          <svg
            viewBox="0 0 33 33"
            role="img"
            aria-label={`QR code for ${landingQrTarget}`}
          >
            <path
              d={landingQrPath}
              stroke="#0d1913"
              strokeWidth={1}
              fill="none"
            />
          </svg>
        </div>
        <div>
          <div className="frl-qrh">Take it to the first tee</div>
          <p>Scan to open on your phone.</p>
          <div className="frl-qrurl">fairwayrivalry.com</div>
        </div>
      </div>
      <div className="frl-micro">
        Live scoring · Rivalry insights · Shared seasons
        <p>
          <a href="/privacy">Privacy</a>
        </p>
      </div>
    </div>
  );
}
